<template>
  <div v-loading="loading" class="order-detail-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="el-icon-document"></i>
            订单详情
          </h1>
          <p class="page-subtitle">订单号：{{ orderDetail.orderNo }}</p>
        </div>
        <div class="action-buttons">
          <el-button v-if="orderDetail.status === 3" type="danger" size="medium" icon="el-icon-refresh-left" @click="showRefundDialog" class="refund-btn">
            退款
          </el-button>
          <el-button size="medium" icon="el-icon-back" @click="goBack" class="back-btn">
            返回
          </el-button>
        </div>
      </div>
    </div>

    <!-- 订单状态卡片 -->
    <el-card class="status-card" shadow="hover">
      <div class="status-content">
        <div class="status-item">
          <span class="status-label">订单状态</span>
          <el-tag :type="getStatusTagType(orderDetail.status)" effect="dark" size="medium" class="status-tag">
            {{ getStatusText(orderDetail.status) }}
          </el-tag>
        </div>
        <div class="status-divider"></div>
        <div class="status-item">
          <span class="status-label">创建时间</span>
          <span class="status-value">{{ formatCreateTime(orderDetail.createTime) }}</span>
        </div>
        <div class="status-divider"></div>
        <div class="status-item">
          <span class="status-label">订单渠道</span>
          <span class="status-value">{{ getOrderChannelText(orderDetail.orderChannel) }}</span>
        </div>
      </div>
    </el-card>

    <!-- 基本信息卡片 -->
    <el-card class="info-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-info"></i>
          基本信息
        </span>
      </div>
      <el-descriptions :column="3" border class="custom-descriptions">
        <el-descriptions-item label="采购订单号">{{ orderDetail.purchaseOrderNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="订单来源">{{ orderDetail.orderSource || '-' }}</el-descriptions-item>
        <el-descriptions-item label="供应商">{{ orderDetail.supplierName || '-' }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 航班信息卡片 -->
    <el-card class="flight-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-plane"></i>
          航班信息
        </span>
      </div>
      <div v-if="!orderDetail.flightInfoList || !orderDetail.flightInfoList.length" class="empty-state">
        <i class="el-icon-info"></i>
        <span>暂无航班信息</span>
      </div>
      <div v-else class="flight-list">
        <div v-for="(flight, index) in orderDetail.flightInfoList" :key="index" class="flight-item">
          <div class="flight-number">{{ flight.flightNo }}</div>
          <div class="flight-route">
            <div class="city-info">
              <div class="city-name">{{ flight.departureCity }}</div>
              <div class="flight-time">{{ formatFlightTime(flight.departureTime) }}</div>
            </div>
            <div class="flight-arrow">
              <i class="el-icon-d-arrow-right"></i>
            </div>
            <div class="city-info">
              <div class="city-name">{{ flight.arriveCity }}</div>
              <div class="flight-time">{{ formatFlightTime(flight.arriveTime) }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 乘机人信息卡片 -->
    <el-card class="passenger-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-user"></i>
          乘机人信息
        </span>
      </div>
      <el-table :data="orderDetail.passengerInfoList || []" border stripe class="custom-table">
        <el-table-column label="姓名" prop="name" width="150px" align="center"></el-table-column>
        <el-table-column label="证件类型" prop="idCardTye" width="120px" align="center">
          <template slot-scope="scope">{{ getIdCardTypeText(scope.row.idCardTye) }}</template>
        </el-table-column>
        <el-table-column label="证件号" prop="idCardNo" min-width="200px" align="center">
          <template slot-scope="scope">{{ scope.row.idCardNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="乘客类型" prop="passengerType" width="100px" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="scope.row.passengerType === 0 ? 'primary' : scope.row.passengerType === 1 ? 'success' : 'warning'">
              {{ getPassengerTypeText(scope.row.passengerType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="票号" prop="ticketNo" width="180px" align="center">
          <template slot-scope="scope">{{ scope.row.ticketNo || '-' }}</template>
        </el-table-column>
        <el-table-column label="证件有效期" prop="certificateEffectTime" width="140px" align="center">
          <template slot-scope="scope">{{ formatCertTime(scope.row.certificateEffectTime) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 联系人信息卡片 -->
    <el-card class="contact-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-phone"></i>
          联系人信息
        </span>
      </div>
      <el-descriptions :column="3" border class="custom-descriptions">
        <el-descriptions-item label="联系人姓名">{{ orderDetail.contacts && orderDetail.contacts.name || '-' }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ orderDetail.contacts && orderDetail.contacts.phone || '-' }}</el-descriptions-item>
        <el-descriptions-item label="邮箱">{{ orderDetail.contacts && orderDetail.contacts.email || '-' }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 价格明细卡片 -->
    <el-card class="price-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-wallet"></i>
          价格明细
        </span>
        <div class="currency-badge">
          <el-tag type="info" size="small">{{ orderDetail.currencyName || '-' }}（{{ orderDetail.currency || '-' }}）</el-tag>
        </div>
      </div>
      <el-empty v-if="!orderDetail.priceInfo" description="暂无价格信息" :image-size="80"></el-empty>
      <div v-else class="price-content">
        <el-row :gutter="20" class="price-row">
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">成人票价</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.adultPrice || '-' }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">成人税费</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.adultTax || '-' }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">成人数量</div>
              <div class="price-value">{{ orderDetail.priceInfo.adultCount || '-' }}</div>
            </div>
          </el-col>
        </el-row>
        <el-row v-if="orderDetail.priceInfo.childCount" :gutter="20" class="price-row">
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">儿童票价</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.childPrice || '-' }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">儿童税费</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.childTax || '-' }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">儿童数量</div>
              <div class="price-value">{{ orderDetail.priceInfo.childCount || '-' }}</div>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="price-row">
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">辅营费用</div>
              <div class="price-value" :class="{ 'negative-amount': orderDetail.priceInfo.auxiliaryPrice < 0 }">
                {{ orderDetail.priceInfo.auxiliaryPrice && orderDetail.priceInfo.auxiliaryPrice < 0 ? '' : (orderDetail.currencySymbol || '') }}{{ orderDetail.priceInfo.auxiliaryPrice || '-' }}
              </div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">票价（总）</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.ticketTotalPrice || '-' }}</div>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="price-item">
              <div class="price-label">税费（总）</div>
              <div class="price-value">{{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.taxPrice || '-' }}</div>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="price-row">
          <el-col :span="24">
            <div class="price-item total-price">
              <div class="price-label">订单总价</div>
              <div class="price-value total-value">
                {{ orderDetail.currencySymbol || '' }}{{ orderDetail.priceInfo.totalPrice || '-' }}
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <!-- 账单流水卡片 -->
    <el-card class="payment-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-bank-card"></i>
          账单流水
        </span>
      </div>
      <el-empty v-if="!orderDetail.paymentInfoList || !orderDetail.paymentInfoList.length" description="暂无账单流水" :image-size="80"></el-empty>
      <el-table v-else :data="orderDetail.paymentInfoList" border stripe class="custom-table">
        <el-table-column label="支付流水号" prop="paymentFlow" min-width="180px" align="center"></el-table-column>
        <el-table-column label="交易类型" prop="cashRebateType" width="120px" align="center">
          <template slot-scope="scope">
            <el-tag :type="getTransactionTagType(scope.row.cashRebateType)" size="small">
              {{ getCashRebateTypeText(scope.row.cashRebateType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="支付方式" prop="payWay" width="120px" align="center">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ getPayWayText(scope.row.payWay) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="金额" prop="payAmount" width="140px" align="center">
          <template slot-scope="scope">
            <span :class="scope.row.payAmount < 0 ? 'negative-amount' : 'positive-amount'">
              {{ scope.row.payAmount && scope.row.payAmount < 0 ? '' : (orderDetail.currencySymbol || '') }}{{ scope.row.payAmount || '-' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="交易时间" prop="createTime" width="180px" align="center"></el-table-column>
      </el-table>
    </el-card>

    <!-- 操作日志卡片 -->
    <el-card class="log-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-edit-outline"></i>
          操作日志
        </span>
      </div>
      <el-empty v-if="!orderDetail.opLogList || !orderDetail.opLogList.length" description="暂无操作日志" :image-size="80"></el-empty>
      <el-table v-else :data="orderDetail.opLogList" border stripe class="custom-table">
        <el-table-column type="index" label="序号" width="80px" align="center"></el-table-column>
        <el-table-column label="操作类型" prop="operationType" width="150px" align="center"></el-table-column>
        <el-table-column label="操作时间" prop="operationTime" width="180px" align="center"></el-table-column>
        <el-table-column label="操作人" prop="operator" width="120px" align="center"></el-table-column>
        <el-table-column label="备注" prop="remark" min-width="200px" align="center"></el-table-column>
      </el-table>
    </el-card>

    <!-- 辅营信息卡片 -->
    <el-card v-if="orderDetail.auxiliaryInfoList && (orderDetail.auxiliaryInfoList.baggage && orderDetail.auxiliaryInfoList.baggage.length || orderDetail.auxiliaryInfoList.seat && orderDetail.auxiliaryInfoList.seat.length || orderDetail.auxiliaryInfoList.meal && orderDetail.auxiliaryInfoList.meal.length)" class="auxiliary-card" shadow="hover">
      <div slot="header" class="card-header">
        <span class="card-title">
          <i class="el-icon-suitcase"></i>
          辅营信息
        </span>
      </div>
      <el-tabs type="border-card" class="custom-tabs">
        <el-tab-pane v-if="orderDetail.auxiliaryInfoList.baggage && orderDetail.auxiliaryInfoList.baggage.length">
          <span slot="label">
            <i class="el-icon-shopping-bag-1"></i> 行李
          </span>
          <el-table :data="orderDetail.auxiliaryInfoList.baggage" border stripe class="custom-table">
            <el-table-column label="乘机人" prop="passengerName" width="150px" align="center"></el-table-column>
            <el-table-column label="行李重量" prop="baggageWeight" width="120px" align="center"></el-table-column>
            <el-table-column label="价格" prop="price" width="120px" align="center">
              <template slot-scope="scope">{{ scope.row.price || '-' }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane v-if="orderDetail.auxiliaryInfoList.seat && orderDetail.auxiliaryInfoList.seat.length">
          <span slot="label">
            <i class="el-icon-seat"></i> 选座
          </span>
          <el-table :data="orderDetail.auxiliaryInfoList.seat" border stripe class="custom-table">
            <el-table-column label="乘机人" prop="passengerName" width="150px" align="center"></el-table-column>
            <el-table-column label="座位号" prop="seatNo" width="120px" align="center"></el-table-column>
            <el-table-column label="价格" prop="price" width="120px" align="center">
              <template slot-scope="scope">{{ scope.row.price || '-' }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane v-if="orderDetail.auxiliaryInfoList.meal && orderDetail.auxiliaryInfoList.meal.length">
          <span slot="label">
            <i class="el-icon-food"></i> 餐食
          </span>
          <el-table :data="orderDetail.auxiliaryInfoList.meal" border stripe class="custom-table">
            <el-table-column label="乘机人" prop="passengerName" width="150px" align="center"></el-table-column>
            <el-table-column label="餐食类型" prop="mealType" width="120px" align="center"></el-table-column>
            <el-table-column label="价格" prop="price" width="120px" align="center">
              <template slot-scope="scope">{{ scope.row.price || '-' }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 退款对话框 -->
    <el-dialog
      title="订单退款"
      :visible.sync="refundDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      custom-class="refund-dialog">
      <div class="dialog-content">
        <el-alert
          title="请注意"
          type="warning"
          description="退款操作将影响订单状态，请谨慎操作"
          :closable="false"
          show-icon
          style="margin-bottom: 20px;">
        </el-alert>
        <el-form :model="refundForm" :rules="refundRules" ref="refundForm" label-width="100px" size="medium">
          <el-form-item label="退款金额" prop="refundAmount">
            <el-input v-model="refundForm.refundAmount" placeholder="请输入退款金额" type="number" prefix-icon="el-icon-money">
              <template slot="append">{{ orderDetail.currency || 'CNY' }}</template>
            </el-input>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="refundDialogVisible = false" size="medium">取 消</el-button>
        <el-button type="primary" @click="handleRefund" size="medium" icon="el-icon-check">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { queryOrderDetail, applyRefund } from '@/api/order/orderApi'

export default {
  name: 'orderDetail',

  data () {
    return {
      loading: false,
      orderNo: '',
      orderDetail: {
        flightInfoList: [],
        passengerInfoList: [],
        contacts: {},
        priceInfo: null,
        opLogList: [],
        paymentInfoList: [],
        auxiliaryInfoList: null,
        cabinInfo: null,
        baggage: null,
        status: null,
      },
      refundDialogVisible: false,
      refundForm: {
        refundAmount: ''
      },
      refundRules: {
        refundAmount: [
          { required: true, message: '请输入退款金额', trigger: 'blur' },
          { pattern: /^(0|[1-9]\d*)(\.\d{1,2})?$/, message: '请输入有效的金额', trigger: 'blur' }
        ]
      }
    }
  },

  methods: {
    // 获取订单详情
    getOrderDetail () {
      this.loading = true
      queryOrderDetail(this.id).then(({ data }) => {
        this.orderDetail = data || {}
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    // 返回
    goBack () {
      this.$router.go(-1)
    },

    // 格式化创建时间
    formatCreateTime (timeArr) {
      if (!timeArr || !Array.isArray(timeArr) || timeArr.length < 6) return '-'
      const [year, month, day, hour, minute, second] = timeArr
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
    },

    // 格式化航班时间
    formatFlightTime (timeStr) {
      if (!timeStr || timeStr.length !== 12) return '-'
      const year = timeStr.substring(0, 4)
      const month = timeStr.substring(4, 6)
      const day = timeStr.substring(6, 8)
      const hour = timeStr.substring(8, 10)
      const minute = timeStr.substring(10, 12)
      return `${year}-${month}-${day} ${hour}:${minute}`
    },

    // 格式化证件有效期
    formatCertTime (timeStr) {
      if (!timeStr || timeStr.length !== 8) return '-'
      const year = timeStr.substring(0, 4)
      const month = timeStr.substring(4, 6)
      const day = timeStr.substring(6, 8)
      return `${year}-${month}-${day}`
    },

    // 获取状态文本
    getStatusText (status) {
      const statusMap = {
        '0': '待支付',
        '1': '支付成功，出票中',
        '2': '支付失败',
        '3': '支付成功，出票失败',
        '4': '出票成功',
        '5': '出票失败，待退款',
        '6': '部分退改',
        '7': '已退改',
        '8': '出票失败，已退款',
        '9': '订单已取消',
        '10': '出票失败，退款失败'
      }
      return statusMap[status] || '-'
    },

    // 获取证件类型文本
    getIdCardTypeText (type) {
      const typeMap = {
        'PP': '护照',
        'GA': '港澳通行证',
        'TW': '台湾通行证',
        'TB': '台胞证',
        'HX': '回乡证',
        'HY': '国际海员证',
        'ID': '身份证',
        'NI': '其他'
      }
      return typeMap[type] || type || '-'
    },

    // 获取乘客类型文本
    getPassengerTypeText (type) {
      const typeMap = {
        0: '成人',
        1: '儿童',
        2: '婴儿'
      }
      return typeMap[type] || '-'
    },

    // 获取订单渠道文本
    getOrderChannelText (channel) {
      const channelMap = {
        '0': '其他',
        '1': '网站',
        '2': 'APP',
        '3': '小程序',
        '4': 'H5',
        '5': '线下'
      }
      return channelMap[channel] || '-'
    },

    // 获取状态标签类型
    getStatusTagType (status) {
      const statusMap = {
        '0': 'success',      // 待支付
        '1': 'success',   // 支付成功，出票中
        '2': 'warning',   // 支付失败
        '3': 'danger',    // 支付成功，出票失败
        '4': 'success',    // 出票成功
        '5': 'danger',    // 出票失败，待退款
        '6': 'success',   // 部分退改
        '7': 'success',   // 已退改
        '8': 'info',      // 出票失败，已退款
        '9': 'info',   // 订单已取消
        '10': 'danger'      // 出票失败，退款失败
      }
      return statusMap[status] || 'info'
    },

    // 获取支付方式文本
    getPayWayText (payWay) {
      const payWayMap = {
        '1': '2c2p',
        '2': '空中云汇',
        '3': '账户余额',
        '4': '微信',
        '5': '支付宝'
      }
      return payWayMap[payWay] || '-'
    },

    // 获取交易类型文本
    getCashRebateTypeText (cashRebateType) {
      const cashRebateTypeMap = {
        4001: '支付出票',
        4002: '出票退款',
        4003: '退票退款',
        4004: '改签补款',
        4005: '改签退款'
      }
      return cashRebateTypeMap[cashRebateType] || '-'
    },

    // 获取交易类型标签颜色
    getTransactionTagType (cashRebateType) {
      const tagTypeMap = {
        4001: 'primary',
        4002: 'warning',
        4003: 'danger',
        4004: 'success',
        4005: 'info'
      }
      return tagTypeMap[cashRebateType] || ''
    },

    // 显示退款对话框
    showRefundDialog () {
      this.refundForm.refundAmount = ''
      this.refundDialogVisible = true
      this.$nextTick(() => {
        this.$refs.refundForm && this.$refs.refundForm.clearValidate()
      })
    },

    // 处理退款
    handleRefund () {
      const vm = this
      this.$refs.refundForm.validate(function (valid) {
        if (valid) {
          const loading = vm.$loading({
            lock: true,
            text: '退款处理中...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })
          applyRefund({
            orderId: vm.id,
            refundAmount: vm.refundForm.refundAmount
          }).then(() => {
            vm.$message.success('退款成功')
            vm.refundDialogVisible = false
            vm.getOrderDetail()
          }).catch(() => {
            vm.$message.error('退款失败')
          }).finally(() => {
            loading.close()
          })
        }
      })
    }
  },

  created () {
    this.id = this.$route.query.id || ''
        this.getOrderDetail()
  }
}
</script>

<style lang="scss" scoped>
.order-detail-container {
  padding: 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  min-height: calc(100vh - 84px);

  // 页面头部
  .page-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    padding: 20px 24px;
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .title-section {
        .page-title {
          margin: 0 0 4px 0;
          font-size: 24px;
          font-weight: 600;
          color: #ffffff;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            font-size: 26px;
          }
        }

        .page-subtitle {
          margin: 0;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 400;
        }
      }

      .action-buttons {
        display: flex;
        gap: 8px;

        .back-btn {
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.3);
          color: #ffffff;

          &:hover {
            background: rgba(255, 255, 255, 0.3);
            border-color: rgba(255, 255, 255, 0.5);
          }
        }

        .refund-btn {
          box-shadow: 0 2px 8px rgba(245, 108, 108, 0.4);
        }
      }
    }
  }

  // 状态卡片
  .status-card {
    margin-bottom: 16px;
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .status-content {
      display: flex;
      align-items: center;
      justify-content: space-around;
      padding: 12px 0;

      .status-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .status-label {
          font-size: 12px;
          color: #909399;
          font-weight: 500;
        }

        .status-value {
          font-size: 14px;
          color: #303133;
          font-weight: 600;
        }

        .status-tag {
          font-size: 13px;
          padding: 4px 12px;
          font-weight: 500;
        }
      }

      .status-divider {
        width: 1px;
        height: 32px;
        background: linear-gradient(180deg, transparent 0%, #dcdfe6 50%, transparent 100%);
      }
    }
  }

  // 通用卡片样式
  .info-card,
  .flight-card,
  .passenger-card,
  .contact-card,
  .price-card,
  .payment-card,
  .log-card,
  .auxiliary-card {
    margin-bottom: 16px;
    border-radius: 8px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      transform: translateY(-1px);
    }

    ::v-deep .el-card__header {
      border-bottom: 1px solid #f0f2f5;
      padding: 14px 18px;
    }

    ::v-deep .el-card__body {
      padding: 16px;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .card-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 6px;

        i {
          color: #667eea;
          font-size: 18px;
        }
      }

      .currency-badge {
        margin-left: auto;
      }
    }
  }

  // 描述列表样式
  .custom-descriptions {
    ::v-deep .el-descriptions__body {
      .el-descriptions__table {
        .el-descriptions-item__label {
          background: #f5f7fa;
          font-weight: 500;
          color: #606266;
          padding: 10px 16px;
        }

        .el-descriptions-item__content {
          color: #303133;
          padding: 10px 16px;
        }
      }
    }

    ::v-deep .el-descriptions__item {
      padding-bottom: 0;
    }
  }

  // 航班信息样式
  .flight-card {
    .empty-state {
      text-align: center;
      padding: 40px 0;
      color: #909399;

      i {
        font-size: 40px;
        margin-bottom: 8px;
        display: block;
      }
    }

    .flight-list {
      .flight-item {
        background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
        border-radius: 6px;
        padding: 12px 16px;
        margin-bottom: 12px;
        border-left: 3px solid #667eea;

        &:last-child {
          margin-bottom: 0;
        }

        .flight-number {
          font-size: 16px;
          font-weight: 600;
          color: #667eea;
          margin-bottom: 10px;
        }

        .flight-route {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .city-info {
            flex: 1;
            text-align: center;

            .city-name {
              font-size: 13px;
              color: #303133;
              font-weight: 500;
              margin-bottom: 4px;
            }

            .flight-time {
              font-size: 12px;
              color: #909399;
            }
          }

          .flight-arrow {
            flex: 0 0 50px;
            text-align: center;
            font-size: 20px;
            color: #667eea;
          }
        }
      }
    }
  }

  // 价格卡片样式
  .price-card {
    .price-content {
      .price-row {
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .price-item {
          background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
          border-radius: 6px;
          padding: 10px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.15);
          }

          &.total-price {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

            .price-label {
              color: rgba(255, 255, 255, 0.9);
            }

            .price-value {
              color: #ffffff;
              font-size: 18px;
            }
          }

          .price-label {
            font-size: 13px;
            color: #606266;
            font-weight: 500;
          }

          .price-value {
            font-size: 16px;
            color: #303133;
            font-weight: 600;

            &.negative-amount {
              color: #f56c6c;
            }
          }
        }
      }
    }
  }

  // 表格样式
  .custom-table {
    ::v-deep .el-table__header-wrapper {
      th {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #ffffff;
        font-weight: 500;
        border: none;
        padding: 10px 0;
      }
    }

    ::v-deep .el-table__body-wrapper {
      tr {
        &:hover > td {
          background-color: #f5f7fa !important;
        }
      }

      td {
        border-color: #f0f2f5;
        padding: 8px 0;
      }
    }

    ::v-deep .el-table__border {
      border-color: #f0f2f5;
    }

    ::v-deep .el-table__row {
      td {
        padding: 8px 0;
      }
    }
  }

  // 金额颜色
  .positive-amount {
    color: #67c23a;
    font-weight: 600;
  }

  .negative-amount {
    color: #f56c6c;
    font-weight: 600;
  }

  // 标签样式
  .el-tag {
    font-weight: 500;
    border: none;
  }

  // 辅营信息标签页
  .custom-tabs {
    ::v-deep .el-tabs__header {
      background: #f5f7fa;
      border-radius: 6px 6px 0 0;
      margin: 0;
    }

    ::v-deep .el-tabs__nav {
      border: none;
    }

    ::v-deep .el-tabs__item {
      border: none;
      color: #606266;
      padding: 0 16px;
      height: 36px;
      line-height: 36px;

      &.is-active {
        color: #667eea;
        background: #ffffff;
      }

      &:hover {
        color: #667eea;
      }
    }

    ::v-deep .el-tab-pane {
      padding: 0;
    }
  }

  // 退款对话框
  ::v-deep .refund-dialog {
    border-radius: 8px;

    .el-dialog__header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: 18px 20px;
      border-radius: 8px 8px 0 0;

      .el-dialog__title {
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
      }

      .el-dialog__headerbtn {
        top: 18px;
        right: 20px;

        .el-dialog__close {
          color: #ffffff;
          font-size: 18px;
        }
      }
    }

    .el-dialog__body {
      padding: 20px;
    }

    .el-dialog__footer {
      padding: 12px 20px;
      border-top: 1px solid #f0f2f5;
    }

    .dialog-content {
      .el-alert {
        border-radius: 6px;
        border: none;
        padding: 10px 16px;
        margin-bottom: 16px;

        ::v-deep .el-alert__title {
          font-size: 14px;
        }

        ::v-deep .el-alert__description {
          font-size: 12px;
        }
      }

      .el-form-item__label {
        color: #606266;
        font-weight: 500;
      }

      .el-form-item {
        margin-bottom: 16px;
      }
    }
  }
}

// 全局滚动条样式
::v-deep ::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep ::-webkit-scrollbar-track {
  background: #f5f7fa;
  border-radius: 3px;
}

::v-deep ::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;

  &:hover {
    background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
  }
}
</style>
